/**
 * Per-campaign lead cap enforcement.
 *
 * Each plan caps how many leads a single campaign may hold
 * (PLAN_FEATURES.maxLeadsPerCampaign — Basic 30, Pro 60, agency unlimited).
 * This is a SEPARATE axis from:
 *   - maxLeadsInDb   — total active leads across the whole account
 *   - maxCampaigns   — how many campaigns the account may have
 *
 * Enforced at every path that points a lead's `campaign_id` at a campaign:
 *   - campaigns worker: POST /campaigns (create wizard) + POST /campaigns/:id/leads (add-leads dialog)
 *   - leads worker:     POST /leads, PUT /leads/:id, POST /leads/bulk when a campaign_id is supplied
 *
 * Counts only ACTIVE (archived_at IS NULL) leads, matching the maxLeadsInDb
 * gate — a soft-deleted lead should not consume a campaign slot.
 */

import { PLAN_FEATURES } from '../constants/plans'
import type { Plan } from '../types/plans'

/**
 * Minimal local declaration of the Cloudflare D1 surface we touch.
 * Avoids pulling @cloudflare/workers-types into the shared package
 * (which the dashboard's browser bundle also consumes). Mirrors the
 * same pattern in credit-notifier.ts.
 */
type D1PreparedStatement = {
  bind(...args: unknown[]): D1PreparedStatement
  first<T = unknown>(): Promise<T | null>
}
type D1Database = {
  prepare(sql: string): D1PreparedStatement
}

export interface CampaignLeadCap {
  /** false once the campaign is at or above its per-plan cap */
  readonly allowed: boolean
  /** active (non-archived) leads currently assigned to the campaign */
  readonly current: number
  /** the plan's per-campaign cap (Infinity = unlimited) */
  readonly limit: number
  /** how many more leads may be added now (0 when full, Infinity = unlimited) */
  readonly remaining: number
}

/** The per-campaign lead cap for a plan tier. Infinity = unlimited (agency). */
export function maxLeadsPerCampaignFor(plan: Plan): number {
  return PLAN_FEATURES[plan].maxLeadsPerCampaign
}

/** Customer-facing plan name (Starter is shown as "Basic"). */
export function planDisplayName(plan: Plan): string {
  return plan === 'starter' ? 'Basic' : plan === 'pro' ? 'Pro' : plan
}

/**
 * The customer-facing "this campaign is full" message, tailored per plan.
 * Numbers are read from PLAN_FEATURES so they never drift from the caps.
 *   - Basic: states the 30 cap and nudges the Pro upgrade (which raises it to 60)
 *   - Pro:   states the 60 cap only (Pro is the top customer-facing tier)
 *   - agency/uncapped: a neutral fallback (this should never surface in practice)
 */
export function campaignCapMessage(plan: Plan): string {
  const proCap = PLAN_FEATURES.pro.maxLeadsPerCampaign
  if (plan === 'starter') {
    const basicCap = PLAN_FEATURES.starter.maxLeadsPerCampaign
    return `This campaign is full. The Basic plan allows ${basicCap} leads per campaign — upgrade to Pro to add up to ${proCap} per campaign.`
  }
  if (plan === 'pro') {
    return `This campaign is full. The Pro plan allows ${proCap} leads per campaign.`
  }
  return 'This campaign has reached its lead limit.'
}

/**
 * Count active leads already in a campaign and compare against the plan's
 * per-campaign cap. Returns allowed=true with limit/remaining = Infinity for
 * uncapped plans (agency) without touching the DB.
 */
export async function checkCampaignLeadCap(
  db: D1Database,
  userId: string,
  campaignId: string,
  plan: Plan,
): Promise<CampaignLeadCap> {
  const limit = maxLeadsPerCampaignFor(plan)
  if (!Number.isFinite(limit)) {
    return { allowed: true, current: 0, limit, remaining: Number.POSITIVE_INFINITY }
  }
  const row = await db
    .prepare(
      `SELECT COUNT(*) AS n FROM leads
        WHERE campaign_id = ? AND user_id = ? AND archived_at IS NULL`,
    )
    .bind(campaignId, userId)
    .first<{ n: number }>()
  const current = row?.n ?? 0
  const remaining = Math.max(0, limit - current)
  return { allowed: current < limit, current, limit, remaining }
}
